import React from 'react';
import coverImg from '../assets/landing/cover.jpg';
import onboardingImg from '../assets/landing/onboarding-bg.webp';
import backgroundImg from '../assets/landing/background.webp';
import gatherLogo from '../assets/landing/gather-logo.png';

export { gatherLogo };

type Tone = 'orange' | 'purple' | 'blue' | 'gradient';
export type CoverVariant = 'cover' | 'onboarding' | 'background';

const COVER_BY_VARIANT: Record<CoverVariant, string> = {
  cover: `url(${coverImg}) center / cover no-repeat`,
  onboarding: `url(${onboardingImg}) center / cover no-repeat`,
  background: `url(${backgroundImg}) center / cover no-repeat`,
};

const tintFor = (tone: Tone): string => {
  switch (tone) {
    case 'orange':
      return 'linear-gradient(135deg, rgba(243,141,76,.55), rgba(234,88,12,.55))';
    case 'purple':
      return 'linear-gradient(135deg, rgba(161,112,250,.55), rgba(87,121,240,.55))';
    case 'blue':
      return 'linear-gradient(135deg, rgba(87,121,240,.55), rgba(53,170,255,.55))';
    case 'gradient':
      return 'linear-gradient(135deg, rgba(243,141,76,.5), rgba(155,93,229,.5) 55%, rgba(87,121,240,.5))';
  }
};

const coverLayer = (variant: CoverVariant, url?: string): string =>
  url ? `url(${url}) center / cover no-repeat` : COVER_BY_VARIANT[variant];

const cardCover = (tone: Tone, variant: CoverVariant = 'cover', url?: string): string =>
  url
    ? // Real cover: show it clean — only dark top/bottom scrims for text legibility, no color tint.
      `linear-gradient(180deg, rgba(0,0,0,.35) 0%, transparent 24%, transparent 52%, rgba(0,0,0,.78) 100%), url(${url}) center / cover no-repeat`
    : `linear-gradient(180deg, rgba(0,0,0,0) 30%, rgba(0,0,0,.8)), ${tintFor(tone)}, ${coverLayer(variant, url)}`;

const postImgCover = (tone: Tone, variant: CoverVariant = 'onboarding', url?: string): string => {
  const softTint = tintFor(tone).replace(/0\.5/g, '0.3').replace(/\.55/g, '.35').replace(/\.5\)/g, '.3)');
  return `${softTint}, ${coverLayer(variant, url)}`;
};

/* ---------- Avatar (mirrors mobile Avatar: initials on hashed pastel) ---------- */
const AVATAR_COLORS = [
  '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', '#DDA0DD', '#98D8C8', '#F7DC6F',
  '#BB8FCE', '#85C1E9', '#F8C471', '#82E0AA', '#F1948A', '#85C1E9', '#F9E79F', '#D7BDE2',
];

const hashColor = (first = '', last = ''): string => {
  const name = `${first}${last}`.toLowerCase();
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

// Real stock portrait photos (Pexels, free license) used as default avatars.
// A name deterministically maps to the same face everywhere on the site.
const AVATAR_PHOTOS = [
  30307610, 12895422, 36720015, 30120595, 38258545, 15324080, 7562179, 5334146,
  14950779, 7561905, 4531837, 4584552, 7408297, 12446392, 36720080, 7625292,
].map(
  (id) =>
    `https://images.pexels.com/photos/${id}/pexels-photo-${id}.jpeg?auto=compress&cs=tinysrgb&w=120&h=120&fit=crop`,
);

const photoForName = (first = '', last = ''): string => {
  const name = `${first}${last}`.toLowerCase();
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_PHOTOS[Math.abs(hash) % AVATAR_PHOTOS.length];
};

type AvatarProps = {
  first?: string;
  last?: string;
  size?: number;
  border?: 'light' | 'none';
  className?: string;
  photo?: string;
};

export type IonIconName =
  | 'chevron-back'
  | 'search'
  | 'bell'
  | 'share'
  | 'chatbubbles'
  | 'settings'
  | 'heart'
  | 'ellipsis';

const ICON_PATHS: Record<IonIconName, React.ReactNode> = {
  'chevron-back': <polyline points="15 18 9 12 15 6" />,
  search: (
    <>
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </>
  ),
  bell: (
    <>
      <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
      <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
    </>
  ),
  share: (
    <>
      <path d="M4 12v7a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-7" />
      <polyline points="16 6 12 2 8 6" />
      <line x1="12" y1="2" x2="12" y2="15" />
    </>
  ),
  chatbubbles: (
    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
  ),
  settings: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </>
  ),
  heart: (
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
  ),
  ellipsis: (
    <>
      <circle cx="5" cy="12" r="1.5" fill="currentColor" stroke="none" />
      <circle cx="12" cy="12" r="1.5" fill="currentColor" stroke="none" />
      <circle cx="19" cy="12" r="1.5" fill="currentColor" stroke="none" />
    </>
  ),
};

export const AppleIcon = ({ size = 24 }: { size?: number }): React.JSX.Element => (
  <svg viewBox="-2 -1 24 24" width={size} height={size} fill="#fff" aria-hidden>
    <path d="M17.523 7.457c-.115.067-2.124 1.204-2.124 3.723 0 2.915 2.556 3.946 2.633 3.972-.012.063-.407 1.407-1.347 2.776-.835 1.205-1.708 2.408-3.036 2.408-1.328 0-1.668-.77-3.2-.77-1.492 0-2.023.795-3.238.795-1.215 0-2.063-1.115-3.036-2.484-1.126-1.608-2.036-4.107-2.036-6.476 0-3.8 2.469-5.815 4.899-5.815 1.293 0 2.371.848 3.185.848.774 0 1.98-.898 3.45-.898.557 0 2.566.051 3.85 1.921zm-4.54-3.514c.607-.72 1.036-1.721 1.036-2.722 0-.139-.012-.279-.037-.393-.988.037-2.163.658-2.872 1.482-.556.633-1.075 1.634-1.075 2.648 0 .153.025.305.037.355.063.012.164.025.265.025.887 0 2.003-.595 2.646-1.395z" />
  </svg>
);

export const GooglePlayIcon = ({ size = 24 }: { size?: number }): React.JSX.Element => (
  <svg viewBox="0 0 32 32" width={size} height={size} aria-hidden>
    <path fill="#00C3FF" d="M4.76 2.51c-.37.28-.59.7-.59 1.23v24.52c0 .53.22.95.59 1.23l13.27-13.84L4.76 2.51z" />
    <path fill="#00F076" d="M22.35 11.43l-4.32-4.32L4.76 2.51 18.03 15.65l4.32-4.22z" />
    <path fill="#FFD012" d="M22.35 20.57l5.24-3.02c1.24-.71 1.24-2.53 0-3.24l-5.24-2.88-4.32 4.22 4.32 4.92z" />
    <path fill="#FF3B30" d="M18.03 15.65L4.76 29.49l13.27-4.6 4.32-4.32-4.32-4.92z" />
  </svg>
);

export const MiniIcon = ({
  name,
  size = 16,
}: {
  name: IonIconName;
  size?: number;
}): React.JSX.Element => (
  <svg
    viewBox="0 0 24 24"
    width={size}
    height={size}
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    {ICON_PATHS[name]}
  </svg>
);

export const MiniIconButton = ({
  name,
  size = 30,
  iconSize,
  variant = 'glass',
}: {
  name: IonIconName;
  size?: number;
  iconSize?: number;
  variant?: 'glass' | 'plain';
}): React.JSX.Element => (
  <div
    className={`mx-iconbtn${variant === 'plain' ? ' mx-iconbtn--plain' : ''}`}
    style={{ width: size, height: size }}
  >
    <MiniIcon name={name} size={iconSize ?? Math.round(size * 0.5)} />
  </div>
);

export const MiniAvatar = ({
  first = '',
  last = '',
  size = 22,
  border = 'light',
  className,
  photo,
}: AvatarProps): React.JSX.Element => (
  <div
    className={`m-ava${border === 'light' ? ' m-ava--bordered' : ''}${className ? ` ${className}` : ''}`}
    style={{
      width: size,
      height: size,
      background: hashColor(first, last),
      fontSize: Math.round(size * 0.4),
    }}
  >
    <img
      src={photo ?? photoForName(first, last)}
      alt={`${first} ${last}`.trim()}
      loading="lazy"
      onError={(e) => {
        e.currentTarget.style.display = 'none';
      }}
      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
    />
  </div>
);

export type EventCardProps = {
  tone?: Tone;
  cover?: CoverVariant;
  coverUrl?: string;
  clubName?: string;
  authorFirst?: string;
  authorLast?: string;
  title: string;
  date: string;
  city: string;
  ribbon?: string;
  ribbonKind?: 'limited' | 'full';
  clubOnly?: boolean;
  cta?: string;
  size?: number;
  attendees?: { first: string; last: string }[];
};

export const MiniEventCard = ({
  tone = 'gradient',
  cover = 'cover',
  coverUrl,
  clubName,
  authorFirst = 'Mia',
  authorLast = 'K.',
  title,
  date,
  city,
  ribbon,
  ribbonKind = 'limited',
  clubOnly,
  cta = 'Join',
  size,
  attendees = [
    { first: 'Tom', last: 'B.' },
    { first: 'Lia', last: 'P.' },
    { first: 'Sam', last: 'R.' },
  ],
}: EventCardProps): React.JSX.Element => (
  <div className="m-event" style={{ background: cardCover(tone, cover, coverUrl), width: size, height: size }}>
    <div className="m-event__top">
      <MiniAvatar first={authorFirst} last={authorLast} size={22} />
      {clubName ? <span className="m-event__owner">{clubName}</span> : null}
      {clubName ? <span className="m-dot" /> : null}
      <span className="m-event__owner">{authorFirst} {authorLast}</span>
    </div>

    {ribbon ? (
      <div className={`m-ribbon${ribbonKind === 'full' ? ' m-ribbon--full' : ''}`}>{ribbon}</div>
    ) : null}

    <div className="m-event__content">
      <div className="m-event__title">{title}</div>
      <div className="m-event__meta">
        <span>{date}</span>
        <span className="m-dot m-dot--lg" />
        <span>{city}</span>
      </div>
      {clubOnly ? <div className="m-event__club-only">Club members only</div> : null}
      <div className="m-event__foot">
        <div className="m-btn m-btn--sm">{cta}</div>
        <div className="m-first-three">
          {attendees.slice(0, 3).map((a, i) => (
            <MiniAvatar key={i} first={a.first} last={a.last} size={18} />
          ))}
        </div>
      </div>
    </div>
  </div>
);

export type ClubCardProps = {
  tone?: Tone;
  cover?: CoverVariant;
  coverUrl?: string;
  ownerFirst?: string;
  ownerLast?: string;
  name: string;
  price?: string;
  members?: string;
  cta?: string;
  joined?: boolean;
  size?: number;
};

export const MiniClubCard = ({
  tone = 'purple',
  cover = 'cover',
  coverUrl,
  ownerFirst = 'Mia',
  ownerLast = 'K.',
  name,
  price = 'Free',
  members,
  cta,
  joined,
  size,
}: ClubCardProps): React.JSX.Element => (
  <div className="m-club" style={{ background: cardCover(tone, cover, coverUrl), width: size, height: size }}>
    <div className="m-event__top">
      <MiniAvatar first={ownerFirst} last={ownerLast} size={22} />
      <span className="m-event__owner">{ownerFirst} {ownerLast}</span>
      <span className="m-pulse" />
    </div>
    <div className="m-event__content">
      <div className="m-club__title">{name}</div>
      <div className="m-event__meta">
        <span className="m-club__price">{price}</span>
        {members ? <span className="m-dot m-dot--lg" /> : null}
        {members ? <span>{members}</span> : null}
      </div>
      <div className={`m-btn m-btn--sm${joined ? ' m-btn--outline' : ''}`}>
        {cta ?? (joined ? 'Joined' : 'Join')}
      </div>
    </div>
  </div>
);

export type ChatMessage = {
  me?: boolean;
  first?: string;
  last?: string;
  text: string;
  time: string;
};

const chatCover = (url: string): string =>
  `linear-gradient(180deg, rgba(11,11,16,.4) 0%, rgba(11,11,16,.2) 18%, rgba(11,11,16,.62) 70%, rgba(11,11,16,.86)), linear-gradient(135deg, rgba(243,141,76,.3), rgba(155,93,229,.3) 55%, rgba(87,121,240,.3)), url(${url}) center / cover no-repeat`;

export const MiniChat = ({
  eventName = 'Rooftop chat',
  participants = '12 participants',
  coverUrl,
  messages,
}: {
  eventName?: string;
  participants?: string;
  coverUrl?: string;
  messages: ChatMessage[];
}): React.JSX.Element => (
  <div className="m-chat" style={coverUrl ? { background: chatCover(coverUrl) } : undefined}>
    <div className="m-chat__header">
      <div className="m-chat__iconbtn" aria-hidden>
        <span className="m-chat__chev">‹</span>
      </div>
      <div className="m-chat__title-pill">
        <span className="m-chat__title">{eventName}</span>
        <span className="m-chat__subtitle">{participants}</span>
      </div>
      <div className="m-chat__iconbtn" aria-hidden>
        <span className="m-chat__bell">
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
            <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
          </svg>
        </span>
      </div>
    </div>
    <div className="m-chat__list">
      {messages.map((m, i) =>
        m.me ? (
          <div key={i} className="m-chat__row m-chat__row--me">
            <div className="m-chat__bubble m-chat__bubble--me">
              <div className="m-chat__text">{m.text}</div>
              <div className="m-chat__time">{m.time}</div>
            </div>
          </div>
        ) : (
          <div key={i} className="m-chat__row">
            <MiniAvatar first={m.first} last={m.last} size={22} />
            <div className="m-chat__bubble">
              {m.first || m.last ? <div className="m-chat__name">{m.first} {m.last}</div> : null}
              <div className="m-chat__text">{m.text}</div>
              <div className="m-chat__time">{m.time}</div>
            </div>
          </div>
        ),
      )}
    </div>
    <div className="m-chat__input">
      <span className="m-chat__plus">+</span>
      <span className="m-chat__placeholder">Message…</span>
      <span className="m-chat__send">
        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="22" y1="2" x2="11" y2="13" />
          <polygon points="22 2 15 22 11 13 2 9 22 2" />
        </svg>
      </span>
    </div>
  </div>
);

export type PostProps = {
  authorFirst: string;
  authorLast: string;
  fromLabel?: string;
  date: string;
  content: string;
  imageTone?: Tone;
  imageCover?: CoverVariant;
  imageUrl?: string;
  likes?: number;
  comments?: number;
};

export const MiniPost = ({
  authorFirst,
  authorLast,
  fromLabel,
  date,
  content,
  imageTone = 'gradient',
  imageCover = 'onboarding',
  imageUrl,
  likes = 48,
  comments = 12,
}: PostProps): React.JSX.Element => (
  <div className="m-post">
    <div className="m-post__head">
      <MiniAvatar first={authorFirst} last={authorLast} size={26} />
      <div className="m-post__user">
        <div className="m-post__name">
          <strong>{authorFirst} {authorLast}</strong>
          {fromLabel ? <span className="m-post__from"> {fromLabel}</span> : null}
        </div>
        <div className="m-post__date">{date}</div>
      </div>
      <div className="m-post__more">⋯</div>
    </div>
    <div className="m-post__body">{content}</div>
    <div className="m-post__image" style={{ background: postImgCover(imageTone, imageCover, imageUrl) }} />
    <div className="m-post__actions">
      <button type="button" className="m-post__act">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
        </svg>
        <span className="m-post__count">{likes}</span>
      </button>
      <button type="button" className="m-post__act">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
        </svg>
        <span className="m-post__count">{comments}</span>
      </button>
      <button type="button" className="m-post__act">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="17 1 21 5 17 9" />
          <path d="M3 11V9a4 4 0 0 1 4-4h14" />
          <polyline points="7 23 3 19 7 15" />
          <path d="M21 13v2a4 4 0 0 1-4 4H3" />
        </svg>
      </button>
    </div>
  </div>
);

export type CreateStep = { label: string; value: string };

export type CreateProps = {
  step?: string;
  fields: CreateStep[];
  tags: { label: string; on?: boolean }[];
};

export const MiniCreate = ({
  step = 'Step 2 · Details',
  fields,
  tags,
}: CreateProps): React.JSX.Element => (
  <div className="m-create">
    <div className="m-create__header">
      <span>New event</span>
      <div className="m-avatar m-avatar--xs" />
    </div>
    <div className="m-create__steps">
      <span className="m-step-dot m-step-dot--on" />
      <span className="m-step-bar m-step-bar--on" />
      <span className="m-step-dot m-step-dot--on" />
      <span className="m-step-bar" />
      <span className="m-step-dot" />
    </div>
    <div className="m-create__title">{step}</div>
    {fields.map((f) => (
      <div key={f.label} className="m-create__field">
        <span>{f.value}</span>
        <small>{f.label}</small>
      </div>
    ))}
    <div className="m-create__tags">
      {tags.map((tag) => (
        <span key={tag.label} className={`m-tag-mini${tag.on ? ' m-tag-mini--on' : ''}`}>
          {tag.label}
        </span>
      ))}
    </div>
    <div className="m-btn m-btn--sm m-btn--full" style={{ marginTop: 'auto' }}>
      Publish
    </div>
  </div>
);
